import * as React from 'react';
import { FormControlLabel, makeStyles } from '@material-ui/core';
import { fontFamilies } from '../../../theme/text';
import { neutral } from '../../../theme/colors';
import { StyledCheckbox } from 'kagr-ui';
import * as styles from './build-segment.module.scss';
import clsx from 'clsx';
import TransactionIndicatorIcon from '../../../images/icons/transaction-indicator-icon';

interface Props {
  id: number | string;
  name: string;
  secondaryText?: string;
  initialChecked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  hidden?: boolean;
  /** checked state that will cause the component to rerender, ex. when an attribute drops into the group because of a */
  forceChecked?: boolean;
  isTransactional?: boolean;
}

const useStyles = makeStyles({
  formControlLabel: {
    marginLeft: 2,
    width: '100%',
    '& .MuiFormControlLabel-label': {
      fontFamily: fontFamilies.heading,
      fontSize: 12,
      fontWeight: 400,
      color: neutral[700],
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  },
  hidden: {
    display: 'none'
  },
  inputLabel: {
    display: 'flex',
    alignItems: 'center'
  }
});

/**
 * Display a single option with a checkbox input in the segment attribute and filter dialog menus.
 *
 * Stores checked state locally to avoid re-rendering all menu items in long list. `onChange` function should avoid changing
 * parent state (ex. altering `useState` member) to avoid re-rendering other checkboxes on change.
 *
 * Care must be taken when conditionally rendering based on a search to hide rather than unmount options so that this
 * is not lost
 */
const AttributeMenuOption = ({
  id,
  name,
  secondaryText,
  initialChecked,
  onChange,
  hidden,
  forceChecked,
  isTransactional
}: Props) => {
  const overrides = useStyles();
  const [checkedState, setCheckedState] = React.useState<boolean>(initialChecked);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setCheckedState(checked);
    onChange(event, checked);
  };

  React.useEffect(() => {
    if (forceChecked !== undefined) {
      setCheckedState(forceChecked);
    }
  }, [forceChecked]);

  return (
    <FormControlLabel
      control={<StyledCheckbox name={name} value={id} edge="start" checked={checkedState} onChange={handleChange} />}
      label={
        <div className={overrides.inputLabel}>
          {isTransactional && (
            <>
              <TransactionIndicatorIcon height="12px" width="12px" />
              &nbsp;
            </>
          )}
          {name.trim() === '' ? <span className={styles.blankOption}>blank</span> : name}&nbsp;
          {secondaryText && `(${secondaryText})`}
        </div>
      }
      title={name}
      className={clsx(overrides.formControlLabel, hidden && overrides.hidden)}
    />
  );
};

export default AttributeMenuOption;
